import axios from 'axios'
import {
  commonHeadersMPWeb,
  getCountryCode,
  getMainLanguageCode,
  mentorApiURL,
} from 'src/mentor/services'

/**
 * Diet plan API client.
 *
 * Three endpoints in play:
 *  - /api/secure/practitioner/diet-plan-templates       (catalogue: list/get/conditions/units)
 *  - /api/secure/practitioner/issued-diet-plans         (CRUD on patient-issued plans)
 *
 * Each helper unwraps the standard `{ data: { data: {...} } }` envelope returned by the server's
 * SuccessResponse and returns the inner data map. Callers handle errors via try/catch on the
 * wrapping promise.
 */

const TEMPLATES_BASE = '/api/secure/practitioner/diet-plan-templates'
const ISSUED_BASE = '/api/secure/practitioner/issued-diet-plans'

function authHeaders() {
  const token = localStorage.getItem('token')
  return {
    Authorization: `Bearer ${token}`,
    ...commonHeadersMPWeb,
    'ef-user-country-code': getCountryCode(),
    'ef-app-language': getMainLanguageCode(),
  }
}

function unwrap(resp) {
  return resp?.data?.data ?? resp?.data ?? null
}

// ---- Catalogue ----

export async function listDietPlanTemplates({ condition, dietaryFlag, language } = {}) {
  const params = new URLSearchParams()
  if (condition) params.set('condition', condition)
  if (dietaryFlag) params.set('dietaryFlag', dietaryFlag)
  if (language) params.set('language', language)
  const url = `${mentorApiURL}${TEMPLATES_BASE}${params.toString() ? `?${params}` : ''}`
  const resp = await axios.get(url, { headers: authHeaders() })
  const data = unwrap(resp) ?? {}
  return {
    templates: Array.isArray(data.dietPlanTemplates) ? data.dietPlanTemplates : [],
    language: data.language ?? language ?? 'en',
  }
}

export async function getDietPlanTemplate(code, language) {
  if (!code) return null
  const params = new URLSearchParams()
  if (language) params.set('language', language)
  const url = `${mentorApiURL}${TEMPLATES_BASE}/${encodeURIComponent(code)}${params.toString() ? `?${params}` : ''}`
  const resp = await axios.get(url, { headers: authHeaders() })
  const data = unwrap(resp) ?? {}
  return data.dietPlanTemplate ?? null
}

export async function listDietPlanConditions() {
  const resp = await axios.get(`${mentorApiURL}${TEMPLATES_BASE}/conditions`, { headers: authHeaders() })
  const data = unwrap(resp) ?? {}
  return Array.isArray(data.conditions) ? data.conditions : []
}

export async function listQuantityUnits() {
  const resp = await axios.get(`${mentorApiURL}${TEMPLATES_BASE}/quantity-units`, { headers: authHeaders() })
  const data = unwrap(resp) ?? {}
  return Array.isArray(data.quantityUnits) ? data.quantityUnits : []
}

// ---- Issued plans ----

export async function saveIssuedDietPlan(plan) {
  if (!plan?.bookedEventId) {
    throw new Error('saveIssuedDietPlan: bookedEventId is required')
  }
  const resp = await axios.post(`${mentorApiURL}${ISSUED_BASE}/save`, plan, { headers: authHeaders() })
  const data = unwrap(resp) ?? {}
  return {
    issuedDietPlan: data.issuedDietPlan ?? null,
    therapyReportId: data.therapyReportId ?? null,
    raw: resp,
  }
}

export async function getIssuedDietPlan(id, language) {
  const params = new URLSearchParams()
  if (language) params.set('language', language)
  const url = `${mentorApiURL}${ISSUED_BASE}/${encodeURIComponent(id)}${params.toString() ? `?${params}` : ''}`
  const resp = await axios.get(url, { headers: authHeaders() })
  const data = unwrap(resp) ?? {}
  return data.issuedDietPlan ?? null
}

export async function listIssuedDietPlansForEvent(eventId) {
  if (!eventId) return []
  const resp = await axios.get(
    `${mentorApiURL}${ISSUED_BASE}/by-event/${encodeURIComponent(eventId)}`,
    { headers: authHeaders() },
  )
  const data = unwrap(resp) ?? {}
  return Array.isArray(data.issuedDietPlans) ? data.issuedDietPlans : []
}

export async function deleteIssuedDietPlan(id) {
  await axios.delete(`${mentorApiURL}${ISSUED_BASE}/${encodeURIComponent(id)}`, { headers: authHeaders() })
}
